/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Rectangle, Point, Container, DisplayObject, utils, Ticker } from 'pixi.js-legacy';
import { fromEvent, Observable, Subject } from 'rxjs';
import { takeUntil, share } from 'rxjs/operators';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { TweenManager } from '@appCanvasCommon/tween/TweenManager';
import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { Ticket } from '@touchplan/common';
import * as ss from 'simple-statistics';
import { PLAN_CONSTANTS } from '@touchplan/common';
import { ICanvasAngularDependencies } from 'src/app/app-data/interfaces/canvas-angular-land';
import { WorldContainer } from '@appCanvasCommon/core/WorldContainer';
import { clamp } from 'utils/basic-utils';
import { LEVEL_OF_DETAIL_TABLE, TICKET_EDIT_COMFORTABLE_ZOOM_LEVEL } from '@appPlanApplication/lod';
import { Easing } from '@appCanvasCommon/tween/Easing';
import { IConnectedCamera } from 'src/app/app-data/interfaces/connected-camera.interface';
import {
	CameraTicketAlignment,
	FocusOnTicketsOptions,
	ViewportChangedEvent,
} from '@appCanvasCommon/interfaces/camera.interface';
/**
 * Enum storing all of the camera effects the camera supports.
 */
export enum CAMERA_EFFECT_TYPES {
	'FLASH',
	'FADE_OUT',
	'FADE_IN',
}

export enum CameraSettings {
	// A somewhat sane value to prevent absurd scale values (0.000001) for trivially small screens.
	MIN_VIEWPORT_HEIGHT_CLAMP = 100,
}

/**
 * A simple camera class that provides helpful methods for manipulating the viewport into the TouchPlan world.
 */
export class Camera extends utils.EventEmitter implements IConnectedCamera {
	public cameraContainer: Container;
	public view: Rectangle;
	public globalView: Rectangle;
	public scale: Point = new Point(0, 0);
	public roundPixels = true;
	public emitter: utils.EventEmitter = new utils.EventEmitter();
	public worldGraphics: FancyGraphics;
	public changed$: Observable<ViewportChangedEvent>;
	public effect: FancyGraphics;
	private _effectDuration = 0;

	private _effectType = 0;
	private canvasAngularLand: ICanvasAngularDependencies;
	/**
	 * Shuts down the camera changed observable
	 */
	private _shutdownSubject: Subject<void> = new Subject();
	private _lastView: Rectangle;
	private _lastScale: Point = new Point(0, 0);
	public tweenManager: TweenManager;
	public isScalingAnimation = false;

	constructor(
		cameraContainer: WorldContainer,
		stage: Container,
		tweenManager: TweenManager,
		x: number,
		y: number,
		width: number,
		height: number,
		canvasAngularLand?: ICanvasAngularDependencies
	) {
		super();
		this.canvasAngularLand = canvasAngularLand;
		this.tweenManager = tweenManager;
		this.cameraContainer = cameraContainer;
		// Create the viewport rectangle
		this.view = new Rectangle(x, y, width, height);
		this._lastView = new Rectangle(x, y, width, height);
		this.globalView = this.getGlobalView();

		this.effect = new FancyGraphics();

		stage.addChild(this.effect);

		this.changed$ = fromEvent<ViewportChangedEvent>(this, 'viewportChanged').pipe(
			takeUntil(this._shutdownSubject),
			share()
		);
	}

	public screenToRealX(x) {
		return x / this.cameraContainer.scale.x - this.view.x / this.cameraContainer.scale.x;
	}
	public screenToRealY(y) {
		return y / this.cameraContainer.scale.y - this.view.y / this.cameraContainer.scale.y;
	}
	public realToScreenX(x) {
		return x * this.cameraContainer.scale.x + this.view.x;
	}
	public realToScreenY(y) {
		return y * this.cameraContainer.scale.y + this.view.y;
	}

	/**
	 * Scales the X coordinate from world space to screen space.
	 * @param x The X coordinate in world space
	 * @returns The X coordinate in screen space
	 */
	public scaleToScreenX(x: number): number {
		return x * this.cameraContainer.scale.x;
	}

	/**
	 * Scales the Y coordinate from world space to screen space.
	 * @param y The Y coordinate in world space
	 * @returns The Y coordinate in screen space
	 */
	public scaleToScreenY(y: number): number {
		return y * this.cameraContainer.scale.y;
	}

	/**
	 * Scales the X coordinate from screen space to world space.
	 * @param x The X coordinate in screen space
	 * @returns The X coordinate in world space
	 */
	public scaleToWorldX(x: number): number {
		return x / this.cameraContainer.scale.x;
	}

	/**
	 * Scales the Y coordinate from screen space to world space.
	 * @param y The Y coordinate in screen space
	 * @returns The Y coordinate in world space
	 */
	public scaleToWorldY(y: number): number {
		return y / this.cameraContainer.scale.y;
	}

	/**
	 * Method that is called before the update loop
	 */
	public preUpdate(delta?: number) {
		// Maybe someday we'll need to do something useful here.
	}
	/**
	 * Returns the current scale value
	 */
	public getScale() {
		return this.cameraContainer.scale;
	}

	public getScreen(): Rectangle {
		return new Rectangle(0, 0, this.view.width, this.view.height);
	}

	public getGlobalView(): Rectangle {
		return new Rectangle(this.screenToRealX(0), this.screenToRealY(0), this.view.width, this.view.height);
	}

	/**
	 * Called each time the renderer updates
	 */
	public update(deltaTimeMs: number = Ticker.shared.elapsedMS) {
		if (this._effectDuration > 0) {
			this.updateEffect(deltaTimeMs);
		}

		if (this.roundPixels) {
			this.view.x = Math.floor(this.view.x);
			this.view.y = Math.floor(this.view.y);
			this.view.width = Math.floor(this.view.width);
			this.view.height = Math.floor(this.view.height);
		}

		// Update the 'world' position
		this.cameraContainer.x = this.view.x;
		this.cameraContainer.y = this.view.y;
		this.cameraContainer.scale.x = this.scale.x;
		this.cameraContainer.scale.y = this.scale.y;

		this.globalView.x = this.screenToRealX(0);
		this.globalView.y = this.screenToRealY(0);
		this.globalView.width = this.screenToRealX(this.view.width) - this.globalView.x;
		this.globalView.height = this.screenToRealY(this.view.height) - this.globalView.y;
	}
	/**
	 * Focus the camera on the passed in DisplayObject
	 */
	public focusOn(displayObject: DisplayObject) {
		this.setPosition(
			Math.round(displayObject.x - this.view.width / 2),
			Math.round(displayObject.y - this.view.height / 2)
		);
	}
	/**
	 * Set the camera position to the provided X and Y coordinates.
	 */
	public setPosition(x: number, y: number, silent = false) {
		this._lastView.x = this.view.x;
		this._lastView.y = this.view.y;
		this.view.x = x;
		this.view.y = y;
		if (!silent) {
			this._dispatchViewChanged();
		}
	}
	/**
	 * Set the scale value of the camera. This will zoom in/zoom out the cameraContainer.
	 */
	public setScale(xScale: number, yScale: number, silent = false) {
		this._lastScale.x = this.scale.x;
		this._lastScale.y = this.scale.y;
		this.scale.x = xScale;
		this.scale.y = yScale;
		if (!silent) {
			this._dispatchViewChanged();
		}
	}

	public setPositionAndScale(x: number, y: number, z: number, z2 = z) {
		this.setPosition(x, y, true);
		this.setScale(z, z2, true);
		this._dispatchViewChanged();
	}

	public tweenScale(scale: number, duration: number = 350, easing = Easing.QUADRATIC.IN_OUT): void {
		this.isScalingAnimation = true;
		this._lastScale.x = this.scale.x;
		this._lastScale.y = this.scale.y;
		this.tweenManager
			.tween(
				this.scale,
				{
					x: scale,
					y: scale,
				},
				duration,
				{
					easing,
					onComplete: () => {
						this.isScalingAnimation = false;
						this._dispatchViewChanged();
					},
				}
			)
			.start();
	}

	public tweenView(x: number, y: number, duration: number = 350, easing = Easing.QUADRATIC.IN_OUT): void {
		this.isScalingAnimation = true;

		this.tweenManager
			.tween(this.view, { x, y }, duration, {
				easing,
				onComplete: () => {
					this.isScalingAnimation = false;
					this._dispatchViewChanged();
				},
			})
			.start();
	}

	public focusOnXY(x: number, y: number): void {
		this.tweenView(x, y);
	}

	public focusOnXYZ(x: number, y: number, z: number): void {
		this.tweenView(x, y);
		this.tweenScale(z);
	}

	public focusOnPoint(point: Point, scale?: number): void {
		this.tweenView(point.x, point.y);
		if (scale) {
			this.tweenScale(scale);
		}
	}

	/**
	 * Provide an X and Y in world space and have the Hud and Camera animate to that position
	 */
	public setPositionAndUpdateHud(x: number, y: number, z?: number): void {
		z = z || this.scale.x;
		const hud = this.canvasAngularLand.hud;
		hud.focusAtPoint(x, y, z);
	}

	public scaleToFit(newPoint: Point, scale: number) {
		this.tweenView(newPoint.x, newPoint.y);
		this.tweenScale(scale);
	}

	public focusOnTicket(ticket: Ticket, options?: FocusOnTicketsOptions): void {
		const hud = this.canvasAngularLand.hud;
		const z = options?.z || hud.z;
		const align = options?.align || CameraTicketAlignment.CENTER;

		let x: number;
		if (align === CameraTicketAlignment.LEFT) {
			x = ticket.view.left - hud.w / z / 2;
		} else if (align === CameraTicketAlignment.RIGHT) {
			x = ticket.view.left - hud.w / z / 2 + ticket.view.width;
		} else {
			x = ticket.view.left - hud.w / z / 2 + ticket.view.width / 2;
		}

		const y: number = ticket.view.top - hud.h / z / 2 + ticket.view.height / 2;
		this.focusOnXYZ(-x * z, -y * z, z || this.scale.x);
		//BEGIN PLACEHOLDER REPLACE AS PART OF TP-6532
		hud.focusAtPoint(x, y, z);
		//END PLACEHOLDER REPLACE AS PART OF TP-6532
	}

	// Currently this is only called from TicketDragService's "dragEnd" in the case
	// where a variance is missing and a variance prompt is displayed.
	public focusOnTickets(list: Map<string, Ticket>): void {
		//BEGIN PLACEHOLDER REPLACE AS PART OF TP-6532
		const v3 = this.getTicketPointsForFocus(list, this.canvasAngularLand.hud.z);
		const hud = this.canvasAngularLand.hud;
		this.focusOnXYZ(-v3.x * v3.z, -v3.y * v3.z, v3.z || this.scale.x);
		//END PLACEHOLDER REPLACE AS PART OF TP-6532
		hud.focusAtPoint(v3.x, v3.y, v3.z);
	}

	/**
	 * Focuses (i.e. pans and zooms) the camera on either the individual selected "combination" ticket
	 * or on the optional "centeringRect" enveloping the selected tickets.
	 *
	 * @param combination The ticket(s) to pan to the center and to zoom in/out on, if necessary
	 * @param z Optional scale factor
	 * @param explicitCenterOffsetY Optional Y offset: if provided, the tickets are shifted up by this amount, after the centering
	 * @param centeringRect Optional rectangle in which to center the ticket(s)
	 */
	public focusOnTicketsFromEdit(
		combination: EditTicketsCombined,
		z?: number,
		explicitCenterOffsetY?: number,
		centeringRect?: Rectangle
	): void {
		const v3 = this.getTicketPointsForFocus(
			combination.selectedList,
			z,
			explicitCenterOffsetY,
			centeringRect
		);
		const hud = this.canvasAngularLand.hud;
		this.focusOnXYZ(-v3.x * v3.z, -v3.y * v3.z, v3.z || this.scale.x);
		hud.focusAtPoint(v3.x, v3.y, v3.z);
	}

	/**
	 * getTicketPointsForFocus returns the point where the camera should focus.
	 * It supports an EditTicketsCombined list or a Map of tickets.
	 */
	private getTicketPointsForFocus(
		list: Map<any, any>,
		explicitZ?: number,
		explicitCenterOffsetY?: number,
		centeringRect?: Rectangle
	) {
		const centerOffsetY = explicitCenterOffsetY || 0;
		let z = explicitZ || 0.5; // The "old" zoom factor, before TP-581

		// TP-581 says:  the zoomed-out farther than the "comfortable" level, zoom-in;
		// otherwise stay at the current zoom level (which is what that "... .hud.z" value is)
		z =
			this.cameraContainer.scale.x < LEVEL_OF_DETAIL_TABLE.COMFORTABLE
				? TICKET_EDIT_COMFORTABLE_ZOOM_LEVEL
				: this.canvasAngularLand.hud.z;
		const left: number[] = [];
		const top: number[] = [];
		const right: number[] = [];
		const bottom: number[] = [];
		list.forEach((ticket: Ticket, key: string) => {
			left.push(ticket.view.left);
			top.push(ticket.view.top);
			right.push(ticket.view.left + ticket.view.width);
			bottom.push(ticket.view.top + ticket.view.height);
		});
		const minX: number = ss.min(left);
		const maxX: number = ss.max(right);
		const minY: number = ss.min(top);
		const maxY: number = ss.max(bottom);
		const width: number = maxX - minX;
		const height: number = maxY - minY;
		const rect = new Rectangle(minX, minY, width, height);

		const thing = this.centerRectToScreen(rect, z, centeringRect);

		const scaledCenterOffsetY = centeringRect ? 0 : centerOffsetY / thing.z;

		return {
			x: thing.rect.x,
			y: thing.rect.y + scaledCenterOffsetY,
			z: thing.z,
		};
	}

	private getCommonValues(rect: Rectangle, scalingRect) {
		const screenRect = scalingRect || this.getScreen();
		const borderSize = 75; //arbitrary size 0.5 inch was requested
		const topStuffHeight = PLAN_CONSTANTS.TOPBAR_BEIGHT + PLAN_CONSTANTS.TIMELINE_DOW_HEIGHT;

		const workingHeight: number = clamp(
			screenRect.height - borderSize / 2 - topStuffHeight - PLAN_CONSTANTS.NEW_EDIT_ARBITRARY_HEIGHT,
			CameraSettings.MIN_VIEWPORT_HEIGHT_CLAMP,
			this.view.height
		);

		const scalingHeight = scalingRect ? scalingRect.height : workingHeight;

		return {
			screenRect,
			borderSize,
			topStuffHeight,
			workingHeight,
			scalingHeight,
		};
	}

	private adjustZValue(rect: Rectangle, z: number, scalingRect) {
		const com = this.getCommonValues(rect, scalingRect);

		// Clamp the revised dimensions to a lower range of 1 to prevent negatives (Which causes the view to render inverse)
		const unclampedRevisedWidth = com.screenRect.width - com.borderSize / 2;
		const unclampedRevisedHeight = com.scalingHeight;

		if (rect.width * z >= unclampedRevisedWidth) {
			z = unclampedRevisedWidth / rect.width;
		}
		if (rect.height * z >= unclampedRevisedHeight) {
			z = unclampedRevisedHeight / rect.height;
		}
		return z;
	}

	private centerRectToScreen(rect: Rectangle, z: number, centeringRect) {
		const adjustedZ = this.adjustZValue(rect, z, centeringRect);
		const screenRect = centeringRect || this.getScreen();

		const rectCenterX: number = rect.x + rect.width / 2;
		const screenCenterX: number = screenRect.width / adjustedZ / 2;
		const x: number = rectCenterX - screenCenterX;

		const rectCenterY: number = rect.y + rect.height / 2;
		const screenCenterY: number = screenRect.height / adjustedZ / 2;
		const y: number = rectCenterY - screenCenterY;

		return {
			rect: new Rectangle(x, y, rect.width, rect.height),
			z: adjustedZ,
		};
	}

	/**
	 * A simple camera effect to make the camera flash.
	 */
	public startFlash(color: number = 0x000000, duration: number = 1000) {
		this.effect.clear();
		this.effect.beginFill(color);
		this.effect.drawRect(0, 0, this.view.width, this.view.height);
		this.effect.alpha = 1;

		this._effectDuration = duration;
		this._effectType = CAMERA_EFFECT_TYPES.FLASH;
		return this;
	}
	/**
	 * A simple camera effect which starts at alpha 0 and lineally fades to the provided color
	 */
	public startFadeOut(color: number = 0x000000, duration: number = 1000) {
		this.effect.clear();
		this.effect.beginFill(color);
		this.effect.drawRect(0, 0, this.view.width, this.view.height);
		this.effect.alpha = 0;
		this._effectDuration = duration;
		this._effectType = CAMERA_EFFECT_TYPES.FADE_OUT;
	}
	/**
	 */
	public startFadeIn(color: number = 0x000000, duration: number = 1000) {
		this.effect.clear();
		this.effect.beginFill(color);
		this.effect.drawRect(0, 0, this.view.width, this.view.height);
		this.effect.alpha = 1;
		this._effectDuration = duration;
		this._effectType = CAMERA_EFFECT_TYPES.FADE_IN;
	}
	/**
	 * Called when the scene is destroyed. Nulls references and destroys the graphics object
	 */
	public destroy() {
		this._shutdownSubject.next();
		this.effect.destroy();
		this.cameraContainer = null;
		this.view = null;
	}
	/**
	 * Called by the update function, updates the state of the current running effect.
	 */
	private updateEffect(deltaTimeMs: number = Ticker.shared.elapsedMS) {
		if (this._effectType === CAMERA_EFFECT_TYPES.FADE_OUT) {
			this.effect.alpha += deltaTimeMs / this._effectDuration;
			if (this.effect.alpha >= 1) {
				this._effectDuration = 0;
				this.effect.alpha = 1;
				this.emit('fadeComplete');
			}
		} else if (this._effectType === CAMERA_EFFECT_TYPES.FADE_IN) {
			this.effect.alpha -= deltaTimeMs / this._effectDuration;
			if (this.effect.alpha >= 1) {
				this._effectDuration = 0;
				this.effect.alpha = 1;
				this.emit('fadeComplete');
			}
		} else if (this._effectType === CAMERA_EFFECT_TYPES.FLASH) {
			this.effect.alpha -= deltaTimeMs / this._effectDuration;
			if (this.effect.alpha <= 0) {
				this._effectDuration = 0;
				this.effect.alpha = 0;
				this.emit('flashComplete');
			}
		}
	}

	public resize(width: number, height: number): Camera {
		this.view.width = width;
		this.view.height = height;
		this.update();
		this._dispatchViewChanged();
		return this;
	}
	// BEGIN PLACEHOLDER: Replace as part of TP-6774
	// Since we are using combineLatest in some areas, and the Timeline can be toggled via a User Preference flag,
	// we have a situation where the timeline won't draw because the camera may have not yet emitted a viewport changed event
	// So we explicitly dispatch a changed event when the timeline is created, otherwise it will draw after the next camera tick
	// This won't affect the timeline in normal operation because camera events will be dispatched _after_ the timeline is loaded.
	public dispatchChanged(): void {
		this._dispatchViewChanged();
	}
	// END PLACEHOLDER
	/**
	 * Intended to be a 1-to-1 replacement of the Hud.ts onTranslate method
	 * @param deltaX the screen space X position to translate the view matrix
	 * @param deltaY the screen space Y position to translate the view matrix
	 */
	public translate(deltaX: number, deltaY: number): void {
		const newX: number = this.view.x + deltaX;
		const newY: number = this.view.y + deltaY;
		this.setPosition(newX, newY);
	}
	/**
	 * Intended to be a 1-to-1 replacement of the Hud.ts onTranslate method
	 * @param point A Point object to transform the view matrix
	 */
	public translateFromPoint(point: Point): void {
		this.translate(point.x, point.y);
	}

	/**
	 * Helper function to dispatch an event every time the viewport changes.
	 */
	private _dispatchViewChanged(): void {
		this.update();
		this.emit('viewportChanged', {
			view: this.view,
			scale: this.scale,
			lastView: this._lastView,
			lastScale: this._lastScale,
		});
	}
}
